// src/hooks/useOptimizer.js - Multi-method portfolio optimization hook
//
// The last result is cached in localStorage for an hour, keyed by user,
// holdings and method, so a page reload does not spend a daily run.

import { useState, useCallback, useEffect, useMemo } from 'react';
import api from '../api';
import { holdingsSignature, readCache, writeCache } from './useQuantAnalysis';

const CACHE_TTL = 60 * 60 * 1000;

const useOptimizer = (user, userProfile) => {
  const [optimizerData, setOptimizerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [lastOptimizeTime, setLastOptimizeTime] = useState(null);
  const [currentMethod, setCurrentMethod] = useState(null);
  const [hydratedKey, setHydratedKey] = useState(null);

  const hasMinHoldings = userProfile?.holdings?.length >= 2;

  const cacheKey = useMemo(() => {
    if (!user?.uid || !userProfile?.holdings) return null;
    return `dd_opt_${user.uid}_${holdingsSignature(userProfile.holdings)}`;
  }, [user?.uid, userProfile?.holdings]);

  useEffect(() => {
    if (!cacheKey) return;
    const cached = readCache(cacheKey);
    if (cached) {
      setOptimizerData(cached.data);
      setLastOptimizeTime(new Date(cached.at));
      setCurrentMethod(cached.method || 'markowitz');
      if (cached.data?.usage) setRateLimitInfo(cached.data.usage);
    } else {
      setOptimizerData(null);
      setLastOptimizeTime(null);
      setCurrentMethod(null);
    }
    setHydratedKey(cacheKey);
  }, [cacheKey]);

  const fetchOptimization = useCallback(async (forceRefresh = false, method = 'markowitz', userViews = null) => {
    if (!forceRefresh && optimizerData && lastOptimizeTime && currentMethod === method) {
      if ((new Date() - lastOptimizeTime) < CACHE_TTL) {
        return optimizerData;
      }
    }

    if (!hasMinHoldings) {
      setError('At least 2 holdings required for portfolio optimization');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        holdings: userProfile.holdings,
        user_profile: {
          ...userProfile,
          isPremium: user?.isPremium || false
        },
        user_id: user?.uid || null,
        method: method
      };

      if (method === 'black_litterman' && userViews && Object.keys(userViews).length > 0) {
        payload.user_views = userViews;
      }

      const response = await api.post('/v1/portfolio/frontier', payload);

      setOptimizerData(response.data);
      setLastOptimizeTime(new Date());
      setCurrentMethod(method);
      if (cacheKey) writeCache(cacheKey, response.data, { method });

      if (response.data.usage) {
        setRateLimitInfo(response.data.usage);
      }

      return response.data;
    } catch (err) {
      console.error('Error running optimization:', err);

      if (err.response?.status === 429) {
        const detail = err.response.data.detail;
        setError(detail.message || 'Daily analysis limit reached');
        setRateLimitInfo({
          remaining: detail.remaining || 0,
          daily_limit: detail.daily_limit || 3,
          user_tier: detail.user_tier || 'free'
        });
      } else {
        const detail = err.response?.data?.detail;
        setError((typeof detail === 'string' ? detail : detail?.message) || err.message || 'Failed to run optimization');
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [optimizerData, lastOptimizeTime, currentMethod, hasMinHoldings, userProfile, user, cacheKey]);

  return {
    optimizerData,
    optimizerLoading: loading,
    optimizerError: error,
    optimizerRateLimitInfo: rateLimitInfo,
    optimizerHydrated: cacheKey !== null && hydratedKey === cacheKey,
    fetchOptimizer: (method, userViews) => fetchOptimization(false, method, userViews),
    refreshOptimizer: (method, userViews) => fetchOptimization(true, method, userViews),
    hasMinHoldings,
    currentMethod,
    clearOptimizerError: () => setError(null)
  };
};

export default useOptimizer;
